import { DIContainer } from "./core/di";

export type Envs = "development" | "production" | "staging";

/**
 * Returns dependency configuration based on environment
 * Dependencies are imported conditionally to ensure tree-shaking:
 * - Production: Real APIs, Sentry, Analytics (imported only when needed)
 * - Development/Staging: Mocks, Console logging (imported only when needed)
 */
const getDependencies = (env: Envs): DIContainer => {
  const isProduction = env === "production";

  if (isProduction) {
    // Production: Real everything
    // Conditionally import production dependencies only when needed
    const SentryLogger = require("./adapters/logger/sentry").default;
    const GtmAnalyticsProvider = require("./adapters/analytics/ga4").default;
    const NexarApi = require("./adapters/nexar-api/actual-api/api").default;
    const RecurlyApi = require("./adapters/recurly-api/actual-api/recurly").default;

    return {
      logger: SentryLogger,
      analytics: GtmAnalyticsProvider(SentryLogger),
      nexarApi: NexarApi,
      recurlyApi: RecurlyApi,
    };
  }

  // Development & Staging: Console logging, configurable API (mock or real)
  // Conditionally import dev dependencies only when needed
  const ConsoleLogger = require("./adapters/logger/console").default;
  const ConsoleAnalyticsProvider = require("./adapters/analytics/console").default;
  const RecurlyApi = require("./adapters/recurly-api/actual-api/recurly").default;
  
  // Check dev config to see if user wants real or mock API
  let nexarApi;
  try {
    const { devConfig } = require("./dev/dev-config");
    const config = devConfig.getConfig();
    
    if (config.useRealApi) {
      // User wants real API in dev/staging
      nexarApi = require("./adapters/nexar-api/actual-api/api").default;
    } else {
      // User wants mock API (default)
      nexarApi = require("./adapters/nexar-api/mock-api/personal-cloud-api").default;
    }
  } catch (error) {
    // Fallback to mock if dev config not available
    console.warn('Failed to load dev config, falling back to mock API:', error);
    nexarApi = require("./adapters/nexar-api/mock-api/personal-cloud-api").default;
  }
  
  return {
    logger: ConsoleLogger,
    analytics: ConsoleAnalyticsProvider(ConsoleLogger),
    nexarApi,
    recurlyApi: RecurlyApi,
  };
};

export default getDependencies;
